import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { UserSidebar } from '@/components/Layout/UserSidebar';
import { dashboardCommonAreasService, DashboardCommonAreasAttribute, DashboardCommonAreasBuilding } from '@/services/dashboardCommonAreasService';
import { streamService } from '@/services/streamService';
import { DateTimePicker } from '@/components/DateTimePicker';
import { computeChartRange, sanitizeChartValue } from '@/utils/chartRanges';
import { formatParisDateTime, localPickerValueToIso, parseApiDateTime } from '@/utils/dateTime';
import { useTheme } from '@/hooks/useTheme';
import { ProjectLogo } from '@/components/ProjectLogo';

export function PartiesCommunesPage() {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const { projectId } = useParams();
  const projectIdInt = projectId ? Number(projectId) : null;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalAttribute, setModalAttribute] = useState<{
    attribute: DashboardCommonAreasAttribute;
    building: DashboardCommonAreasBuilding['building'];
    project: DashboardCommonAreasBuilding['project'];
    deviceName: string | null;
    boxName: string | null;
  } | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'parties-communes', projectIdInt],
    queryFn: () => dashboardCommonAreasService.getCommonAreas(),
  });

  const buildings = useMemo(() => {
    const all = data ?? [];
    if (!projectIdInt) return [];
    return all.filter((b) => b.project.id === projectIdInt);
  }, [data, projectIdInt]);
  
  // Get current project from first building if available
  const currentProject = buildings.length > 0 ? buildings[0].project : null;

  if (!projectIdInt) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <span className="material-icons-outlined text-red-500 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Projet requis</h2>
            <p className="text-slate-500 dark:text-slate-400 mb-4">Veuillez sélectionner un projet pour accéder aux parties communes.</p>
            <button
              onClick={() => navigate('/dashboard', { replace: true })}
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Choisir un projet
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDateTime = (iso: string | null) => {
    if (!iso) return '-';
    try {
      return formatParisDateTime(iso);
    } catch {
      return '-';
    }
  };

  const AttributeGraphModal = ({
    open,
    onClose,
    attribute,
    title,
  }: {
    open: boolean;
    onClose: () => void;
    attribute: DashboardCommonAreasAttribute;
    title: string;
  }) => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
    const [dateFrom, setDateFrom] = useState<string>(() => monthStart.toISOString().slice(0, 16));
    const [dateTo, setDateTo] = useState<string>(() => now.toISOString().slice(0, 16));
    const [isExporting, setIsExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(0);

    const fromISO = useMemo(() => localPickerValueToIso(dateFrom) ?? null, [dateFrom]);

    const toISO = useMemo(() => {
      if (!dateTo) return null;
      const [datePart, timePart] = dateTo.split('T');
      const [hours = 0, minutes = 0] = (timePart || '').split(':').map(Number);
      if (hours === 0 && minutes === 0) {
        return localPickerValueToIso(`${datePart}T23:59`) ?? null;
      }
      return localPickerValueToIso(dateTo) ?? null;
    }, [dateTo]);

    const unit = attribute.type?.unit || '';

    // Get project ID from current project
    const projectId = currentProject?.id;

    // Format date for filename
    const formatDateForFilename = (dateStr: string): string => {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return 'date';
      return d.toISOString().slice(0, 10).replace(/-/g, '');
    };

    const handleExport = async () => {
      if (!fromISO || !toISO) {
        return;
      }

      setIsExporting(true);
      setExportProgress(0);

      try {
        // Fetch all streams for export
        setExportProgress(10);
        const allStreams = await streamService.getAllStreamsByAttribute(attribute.id, {
          itemsPerPage: 500,
          order: { timestamp: 'ASC' },
          from: fromISO,
          to: toISO,
        });

        setExportProgress(50);

        // Generate CSV content
        const csvRows: string[] = [];
        
        // UTF-8 BOM for Windows compatibility
        csvRows.push('\uFEFF');
        
        // Header
        csvRows.push(['ATTRIBUT', 'VALEUR', 'DATE', 'UNITE'].join(';'));

        // Data rows
        for (const stream of allStreams) {
          const dateFormatted = formatParisDateTime(stream.timestamp, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });
          
          const attributeName = attribute.name || 'Attribut';
          const value = stream.value || '';
          const unitValue = unit || '';
          
          csvRows.push([attributeName, value, dateFormatted, unitValue].join(';'));
        }

        setExportProgress(90);

        // Create blob and download
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=UTF-8' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Generate filename: attribute-name_date-from_date-to.csv
        const sanitizeFilename = (str: string) => {
          return str
            .replace(/[<>:"/\\|?*]/g, '') // Remove invalid filename characters
            .replace(/\s+/g, '_') // Replace spaces with underscores
            .trim();
        };
        const attributeName = sanitizeFilename(attribute.name || 'attribut');
        const dateFromFormatted = formatDateForFilename(dateFrom);
        const dateToFormatted = formatDateForFilename(dateTo);
        const filename = `export_${attributeName}_${dateFromFormatted}_${dateToFormatted}.csv`;
        
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setExportProgress(100);
        setTimeout(() => {
          setIsExporting(false);
          setExportProgress(0);
        }, 500);
      } catch (error: any) {
        console.error('Export error:', error);
        setIsExporting(false);
        setExportProgress(0);
        const errorMessage = error?.message || 'Erreur lors de l\'export. Veuillez réessayer.';
        alert(`Erreur lors de l'export: ${errorMessage}`);
      }
    };

    const { data: streams, isLoading: isLoadingStreams } = useQuery({
      queryKey: ['streams', attribute.id, fromISO, toISO],
      queryFn: () =>
        streamService.getAllStreamsByAttribute(attribute.id, {
          itemsPerPage: 500,
          order: { timestamp: 'ASC' },
          from: fromISO ?? undefined,
          to: toISO ?? undefined,
        }),
      enabled: open && !!attribute.id && !!fromISO && !!toISO,
    });

    const chartData = useMemo(() => {
      const raw = streams ?? [];
      return raw
        .map((s) => ({
          timestamp: parseApiDateTime(s.timestamp).getTime(),
          value: sanitizeChartValue(
            Number.parseFloat(s.value),
            title,
            attribute.type?.unit ?? null,
            attribute.type?.code ?? null
          ),
          label: formatParisDateTime(s.timestamp, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
        }))
        .filter((p) => Number.isFinite(p.timestamp) && Number.isFinite(p.value));
    }, [attribute.type?.code, attribute.type?.unit, streams, title]);

    const { min: minValue, max: maxValue, range } = computeChartRange(
      chartData.map((d) => d.value),
      title,
      attribute.type?.unit ?? null,
      attribute.type?.code ?? null
    );
    const chartHeight = 300;
    const chartWidth = 800;
    const padding = 40;

    const [hoverIndex, setHoverIndex] = useState<number | null>(null);
    const getX = (index: number) => padding + (index / (chartData.length - 1 || 1)) * (chartWidth - 2 * padding);
    const getY = (value: number) => {
      const clamped = Math.max(minValue, Math.min(maxValue, value));
      return chartHeight - padding - ((clamped - minValue) / range) * (chartHeight - 2 * padding);
    };

    const pathData =
      chartData.length > 0
        ? chartData
            .map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.value)}`)
            .join(' ')
        : '';

    const hoveredPoint = useMemo(() => {
      if (hoverIndex === null) return null;
      const p = chartData[hoverIndex];
      if (!p) return null;
      return {
        ...p,
        x: getX(hoverIndex),
        y: getY(p.value),
      };
    }, [chartData, hoverIndex]);

    const handleChartMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
      if (chartData.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * chartWidth;
      const clampedX = Math.max(padding, Math.min(chartWidth - padding, x));
      const ratio = (clampedX - padding) / (chartWidth - 2 * padding);
      const idx = Math.round(ratio * (chartData.length - 1));
      const safeIdx = Math.max(0, Math.min(chartData.length - 1, idx));
      setHoverIndex(safeIdx);
    };

    const handleChartMouseLeave = () => setHoverIndex(null);

    if (!open) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
        <div
          className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl max-w-5xl w-full mx-4 max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-slate-200 dark:border-slate-700 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs text-slate-500 dark:text-slate-400">Graphe</p>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white truncate">{title}</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{attribute.name}</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700"
            >
              <span className="material-icons-outlined">close</span>
            </button>
          </div>

          <div className="p-5 border-b border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <DateTimePicker label="Du" value={dateFrom} onChange={setDateFrom} />
              <DateTimePicker label="Au" value={dateTo} onChange={setDateTo} />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => {
                  const now = new Date();
                  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                  const fromStr = yesterday.toISOString().slice(0, 16);
                  const toStr = now.toISOString().slice(0, 16);
                  setDateFrom(fromStr);
                  setDateTo(toStr);
                }}
                className="px-4 py-2 bg-primary/20 border border-primary/30 text-primary rounded-lg text-sm font-semibold hover:bg-primary/30 transition-colors whitespace-nowrap"
              >
                Dernières 24h
              </button>
              {!isExporting && (
                <button
                  onClick={handleExport}
                  disabled={!fromISO || !toISO || !projectId}
                  className={`px-4 py-2 border rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 whitespace-nowrap ${
                    !fromISO || !toISO || !projectId
                      ? 'bg-slate-100 dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed opacity-60'
                      : 'bg-emerald-600/20 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-600/30'
                  }`}
                >
                  <span className="material-icons-outlined" style={{ fontSize: '18px' }}>download</span>
                  <span>Exporter</span>
                </button>
              )}
            </div>
            {isExporting && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-500 dark:text-slate-400">Génération du fichier CSV...</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{Math.round(exportProgress)}%</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-primary h-full transition-all duration-300 ease-out rounded-full"
                    style={{ width: `${exportProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="p-5 flex-1 overflow-y-auto">
            {isLoadingStreams ? (
              <div className="text-slate-500 dark:text-slate-400 flex items-center gap-2">
                <span className="material-icons-outlined animate-spin">progress_activity</span>
                Chargement des données...
              </div>
            ) : chartData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400 gap-2">
                <span>Aucune donnée à afficher sur la période.</span>
                {(attribute?.currentValue != null && attribute.currentValue !== '') || attribute?.currentValueTs ? (
                  <div className="text-sm text-slate-500 dark:text-slate-400 text-center">
                    {attribute?.currentValue != null && attribute.currentValue !== '' && (
                      <div>Dernière valeur : <span className="font-semibold text-slate-700 dark:text-slate-300">{attribute.currentValue}{unit ? ` ${unit}` : ''}</span></div>
                    )}
                    {attribute?.currentValueTs && (
                      <div>Date : {formatParisDateTime(attribute.currentValueTs)}</div>
                    )}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="bg-slate-50 dark:bg-slate-950/30 border border-slate-200 dark:border-slate-700 rounded-xl p-4 overflow-x-auto">
                <svg
                  width={chartWidth}
                  height={chartHeight}
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  className="select-none"
                  onMouseMove={handleChartMouseMove}
                  onMouseLeave={handleChartMouseLeave}
                >
                  {/* Grid lines and Y-axis labels */}
                  {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
                    const y = padding + ratio * (chartHeight - 2 * padding);
                    const value = minValue + (1 - ratio) * range;
                    return (
                      <g key={ratio}>
                        <line x1={padding} y1={y} x2={chartWidth - padding} y2={y} stroke={isDark ? 'rgba(148, 163, 184, 0.1)' : 'rgba(100, 116, 139, 0.15)'} strokeWidth="1" />
                        <text x={padding - 10} y={y + 4} fill={isDark ? '#94a3b8' : '#475569'} fontSize="10" textAnchor="end">
                          {value.toFixed(1)}{unit ? ` ${unit}` : ''}
                        </text>
                      </g>
                    );
                  })}

                  {/* axes */}
                  <line x1={padding} y1={chartHeight - padding} x2={chartWidth - padding} y2={chartHeight - padding} stroke={isDark ? '#334155' : '#cbd5e1'} />
                  <line x1={padding} y1={padding} x2={padding} y2={chartHeight - padding} stroke={isDark ? '#334155' : '#cbd5e1'} />

                  {/* line */}
                  <path d={pathData} fill="none" stroke="#fb923c" strokeWidth="2.5" />

                  {/* x labels: 0%, 25%, 50%, 75%, 100% */}
                  {[0, 0.25, 0.5, 0.75, 1].map((t) => {
                    if (chartData.length === 0) return null;
                    const idx = Math.round(t * (chartData.length - 1));
                    const p = chartData[idx];
                    if (!p) return null;
                    const x = getX(idx);
                    const label = formatParisDateTime(p.timestamp, { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
                    return (
                      <text key={t} x={x} y={chartHeight - 12} textAnchor="middle" fontSize="10" fill={isDark ? '#94a3b8' : '#475569'}>
                        {label}
                      </text>
                    );
                  })}

                  {/* hover elements */}
                  {hoveredPoint && (
                    <>
                      <line x1={hoveredPoint.x} y1={padding} x2={hoveredPoint.x} y2={chartHeight - padding} stroke="#fb923c" strokeOpacity="0.35" />
                      <circle cx={hoveredPoint.x} cy={hoveredPoint.y} r="4.5" fill="#fb923c" stroke={isDark ? '#0f172a' : '#f8fafc'} strokeWidth="2" />
                      <g transform={`translate(${Math.min(chartWidth - 260, hoveredPoint.x + 10)}, ${Math.max(10, hoveredPoint.y - 36)})`}>
                        <rect width="250" height="44" rx="10" fill={isDark ? '#0b1220' : '#f8fafc'} stroke={isDark ? '#334155' : '#cbd5e1'} />
                        <text x="12" y="18" fontSize="12" fill={isDark ? '#e2e8f0' : '#334155'}>
                          {hoveredPoint.value.toFixed(2)}{unit ? ` ${unit}` : ''}
                          {(hoveredPoint.value < minValue || hoveredPoint.value > maxValue) ? ' (hors plage)' : ''}
                        </text>
                        <text x="12" y="34" fontSize="10" fill={isDark ? '#94a3b8' : '#64748b'}>
                          {hoveredPoint.label}
                        </text>
                      </g>
                    </>
                  )}
                </svg>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined animate-spin text-primary text-6xl mb-4">progress_activity</span>
            <p className="text-slate-500 dark:text-slate-400">Chargement des parties communes...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-xl px-6">
            <span className="material-icons-outlined text-red-400 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Erreur</h2>
            <p className="text-slate-400">Impossible de charger les devices “parties communes”.</p>
          </div>
        </div>
      </div>
    );
  }

  if (buildings.length === 0) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <span className="material-icons-outlined text-slate-400 text-6xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Aucune partie commune</h2>
            <p className="text-slate-400">Aucun device catégorisé <b>PARTIES_COMMUNES</b> n’a été trouvé sur vos projets.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 text-slate-900 dark:bg-slate-900 dark:text-slate-100 h-screen flex overflow-hidden font-display">
      {/* Sidebar */}
      <UserSidebar projectId={projectIdInt} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden relative bg-slate-50 dark:bg-slate-900">
        {/* Header */}
        <header className="h-16 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between px-6 sm:px-8 z-20 shadow-sm sticky top-0">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 text-slate-400 hover:text-slate-900 dark:hover:text-white rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
            >
              <span className="material-icons-outlined" style={{ fontSize: '20px' }}>menu</span>
            </button>
            <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
              Parties communes
            </h1>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700 mx-2"></div>
            <div className="flex items-center text-sm text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/50 px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700/50">
              <span className="material-icons-outlined mr-2 text-slate-500 dark:text-slate-500" style={{ fontSize: '16px' }}>domain</span>
              {buildings.length} immeuble{buildings.length > 1 ? 's' : ''}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="hidden md:flex items-center px-3 py-1 bg-slate-100 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-full text-xs font-medium text-slate-600 dark:text-slate-300">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse shadow-sm shadow-green-500/50"></span>
              Système en ligne
            </div>
            <div className="h-8 w-px bg-slate-200 dark:bg-slate-700 mx-1"></div>
            <ProjectLogo project={currentProject} size="md" className="rounded-lg" />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-primary transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
              title={isDark ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {isDark ? <span className="material-icons-outlined">light_mode</span> : <span className="material-icons-outlined">dark_mode</span>}
            </button>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900">
          <div className="space-y-8">
            {buildings.map((group) => (
              <section key={`${group.project.id}-${group.building.id}`} className="space-y-4">
                <div className="flex items-end justify-between gap-4">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-slate-900 dark:text-white truncate">{group.building.name ?? 'Immeuble'}</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                      {group.project.title}
                      {group.building.code ? ` • ${group.building.code}` : ''}
                    </p>
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/60 rounded-lg px-3 py-1.5">
                    {group.devices.length} device{group.devices.length > 1 ? 's' : ''}
                  </div>
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {group.devices.map((device) => (
                    <div
                      key={device.id}
                      className="relative overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800/40 p-5"
                    >
                      {/* Decorative chart background */}
                      <svg
                        viewBox="0 0 100 24"
                        className="absolute inset-0 h-full w-full stroke-orange-400 opacity-10"
                        preserveAspectRatio="none"
                      >
                        <polyline fill="none" strokeWidth="2" points="0,18 10,16 20,17 30,10 40,12 50,7 60,9 70,6 80,8 90,4 100,6" />
                      </svg>

                      <div className="relative">
                        <div className="flex items-start justify-between gap-4">
                          <div className="min-w-0">
                            <p className="text-xs text-slate-500 dark:text-slate-400">Device</p>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white truncate">{device.name ?? `Device #${device.id}`}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                              {device.box?.name ? `Box: ${device.box.name}` : 'Box: -'}
                            </p>
                          </div>
                          <div className="text-xs text-orange-200 bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-1.5 whitespace-nowrap">
                            {device.attributes.length} attribut{device.attributes.length > 1 ? 's' : ''}
                          </div>
                        </div>

                        <div className="mt-4 overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700/70">
                          <div className="grid grid-cols-3 bg-slate-50 dark:bg-slate-900/40 text-xs font-semibold text-slate-600 dark:text-slate-300 px-3 py-2">
                            <div>Nom</div>
                            <div className="text-right">Valeur</div>
                            <div className="text-right">Date</div>
                          </div>
                          <div className="divide-y divide-slate-200 dark:divide-slate-700/70">
                            {device.attributes.length === 0 ? (
                              <div className="px-3 py-3 text-sm text-slate-500 dark:text-slate-400">Aucun attribut</div>
                            ) : (
                              device.attributes.map((attr) => (
                                <button
                                  key={attr.id}
                                  onClick={() => {
                                    setModalAttribute({
                                      attribute: attr,
                                      building: group.building,
                                      project: group.project,
                                      deviceName: device.name,
                                      boxName: device.box?.name ?? null,
                                    });
                                    setModalOpen(true);
                                  }}
                                  className="w-full text-left grid grid-cols-3 px-3 py-2.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                                >
                                  <div className="min-w-0">
                                    <div className="truncate text-slate-800 dark:text-slate-100">{attr.name}</div>
                                    {attr.type?.type ? (
                                      <div className="text-xs text-slate-500 dark:text-slate-400 truncate">{attr.type.type}</div>
                                    ) : null}
                                  </div>
                                  <div className="text-right font-semibold text-slate-800 dark:text-slate-100">
                                    {attr.currentValue ?? '-'}
                                  </div>
                                  <div className="text-right text-slate-500 dark:text-slate-400">{formatDateTime(attr.currentValueTs)}</div>
                                </button>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </div>
      </div>

      {modalOpen && modalAttribute ? (
        <AttributeGraphModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setModalAttribute(null);
          }}
          attribute={modalAttribute.attribute}
          title={`${modalAttribute.project.title} • ${modalAttribute.building.name ?? 'Immeuble'} • ${modalAttribute.deviceName ?? 'Device'}`}
        />
      ) : null}
    </div>
  );
}
